import { useIsMobile } from "@/hooks/use-mobile";
import { useEffect, useState, useRef } from "react";
import { useTheme } from "next-themes";

const AnimatedBackground = () => {
  const isMobile = useIsMobile();
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [isScrolling, setIsScrolling] = useState(false);
  const scrollTimerRef = useRef<number>(0);

  // Pause animations while user is actively scrolling to prevent jank
  useEffect(() => {
    if (isMobile) return; // No bubbles on mobile anyway

    const handleScroll = () => {
      if (!isScrolling) setIsScrolling(true);
      clearTimeout(scrollTimerRef.current);
      scrollTimerRef.current = window.setTimeout(() => {
        setIsScrolling(false);
      }, 150);
    };

    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", handleScroll);
      clearTimeout(scrollTimerRef.current);
    };
  }, [isMobile, isScrolling]);

  return (
    <div className="fixed inset-0 overflow-hidden pointer-events-none -z-10">
      {/* Base gradient — adapts to theme */}
      <div className={`absolute inset-0 ${
        isDark
          ? 'bg-black'
          : 'bg-gradient-to-b from-emerald-50 via-green-50 to-teal-100'
      }`} />
      
      {/* Reduced set of bubbles, paused during scroll */}
      {!isMobile && (
        <div style={{ animationPlayState: isScrolling ? "paused" : "running" }}>
          <div
            className={`absolute w-96 h-96 rounded-full blur-3xl will-change-transform animate-bubble-float-1 ${
              isDark
                ? 'bg-gradient-to-br from-emerald-900/15 to-green-900/8'
                : 'bg-gradient-to-br from-emerald-300/25 to-green-400/15'
            }`}
            style={{ left: '5%', bottom: '-15%', animationPlayState: 'inherit' }}
          />
          <div
            className={`absolute w-72 h-72 rounded-full blur-3xl will-change-transform animate-bubble-float-2 ${
              isDark
                ? 'bg-gradient-to-br from-teal-900/12 to-emerald-900/6'
                : 'bg-gradient-to-br from-teal-300/20 to-emerald-400/10'
            }`}
            style={{ right: '10%', bottom: '-5%', animationPlayState: 'inherit' }}
          />
          <div
            className={`absolute w-56 h-56 rounded-full blur-2xl will-change-transform animate-bubble-float-3 ${
              isDark
                ? 'bg-gradient-to-br from-green-900/15 to-teal-900/8'
                : 'bg-gradient-to-br from-green-300/25 to-teal-300/15'
            }`}
            style={{ left: '25%', bottom: '0%', animationPlayState: 'inherit' }}
          />
          <div
            className={`absolute w-48 h-48 rounded-full blur-2xl will-change-transform animate-bubble-float-1 ${
              isDark
                ? 'bg-gradient-to-br from-emerald-900/18 to-green-900/10'
                : 'bg-gradient-to-br from-emerald-200/30 to-green-300/20'
            }`}
            style={{ right: '30%', bottom: '5%', animationPlayState: 'inherit' }}
          />
          <div
            className={`absolute w-36 h-36 rounded-full blur-xl will-change-transform animate-bubble-float-4 ${
              isDark
                ? 'bg-gradient-to-br from-teal-900/20 to-green-900/10'
                : 'bg-gradient-to-br from-teal-200/35 to-green-200/20'
            }`}
            style={{ left: '8%', bottom: '10%', animationPlayState: 'inherit' }}
          />
          <div
            className={`absolute w-64 h-64 rounded-full blur-3xl will-change-transform animate-bubble-float-2 ${
              isDark
                ? 'bg-gradient-to-br from-emerald-900/12 to-teal-900/6'
                : 'bg-gradient-to-br from-emerald-200/20 to-teal-300/12'
            }`}
            style={{ right: '0%', bottom: '-8%', animationPlayState: 'inherit' }}
          />
        </div>
      )}
    </div>
  );
};

export default AnimatedBackground;
